import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { LockOutlined, UserOutlined } from '@ant-design/icons';
import { Button, Form, Input, Typography, message, Divider } from 'antd';
import { useNavigate } from 'react-router-dom';
import { login, loginWithFacebook } from './LoginServices';
import { useGlobal } from '../Shared/GlobalContext/GlobalContext';
import { saveAuthData, getToken } from '../../Config/ApiConfig';
import { isFacebookSDKLoaded, loginWithFacebookSdk } from '../../Utils/facebook';
import { useFacebookSDK } from '../../Hooks/useFacebookSDK';
import { facebookLoginScope } from '../../Config/facebookConfig';
import Turnstile from 'react-turnstile';
import { useTheme } from '../Shared/GlobalContext/ThemeContext';
import useTurnstileScale from '../../Hooks/useTurnstileScale';
import i18n from '../../i18n';
import { resolveBackendMessage } from '../../Utils/apiMessages';
import { mapTurnstileLanguage } from '../../Utils/turnstile';

import CommonWrapper from '../Shared/CommonWrapper/CommonWrapper';

const { Text } = Typography;

const Login = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loginType, setLoginType] = React.useState('');
  const [captchaToken, setCaptchaToken] = React.useState('');
  useFacebookSDK(1);
  const { loading, setLoading } = useGlobal();
  const siteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY;
  const { isDarkMode } = useTheme();
  const turnstileRef = React.useRef(null);
  useTurnstileScale(turnstileRef, {}, [isDarkMode]);
  const captchaLang = mapTurnstileLanguage(i18n.language);

  const onFinish = useCallback(async (values) => {
    try {
      setLoginType('platform');
      setLoading(true);
      if (!captchaToken) {
        message.error(t('please_verify_captcha'));
        return;
      }
      const { data } = await login({
        email: values.username,
        password: values.password,
        'turnstile_token': captchaToken
      }, captchaToken);
      saveAuthData(data);
      navigate('/');
    } catch (error) {
      const payload = error?.response?.data;
      const resolved = resolveBackendMessage(t, payload);
      if (resolved) {
        message.error(resolved);
      } else if (error.response?.status === 401) {
        message.error(t('invalid_email_or_password'));
      } else {
        message.error(t('an_error_occurred_during_login'));
      }
    } finally {
      setLoading(false);
      setLoginType('');
    }
  }, [navigate, setLoading, t, captchaToken]);

  const loginWithFacebookCallback = useCallback(async (access_token) => {
    setLoading(true);
    setLoginType('facebook');
    try {
      const { data } = await loginWithFacebook(access_token);
      if (!data.access_token) {
        navigate('/accept-terms', { state: { user: data.user, access_token } });
      } else {
        saveAuthData(data);
        navigate('/');
      }
    } catch (error) {
      const payload = error?.response?.data;
      const resolved = resolveBackendMessage(t, payload);
      if (resolved) {
        message.error(resolved);
      } else {
        message.error(t('an_error_occurred_during_login'));
      }
    } finally {
      setLoading(false);
      setLoginType('');
    }
  }, [navigate, setLoading, t]);


  useEffect(() => {
    const token = getToken();
    if (token) {
      navigate('/');
      return;
    }

  }, [navigate]);

  const handleFacebookLogin = useCallback(() => {
    if (!isFacebookSDKLoaded()) {
      message.error(t('facebook_sdk_not_loaded'));
      return;
    }

    setLoading(true);
    loginWithFacebookSdk((response) => {
      if (response?.error) {
        message.error(t('facebook_sdk_not_loaded'));
        setLoading(false);
        return;
      }
      if (response?.authResponse?.accessToken) {
        loginWithFacebookCallback(response.authResponse.accessToken);
      } else if (response?.status === 'not_authorized' || response?.status === 'unknown') {
        const errorMsg = response?.status === 'unknown' ? t('facebook_requires_2fa', { defaultValue: 'Please complete the Facebook security check in the popup and click Continue with Facebook again.' }) : t('an_error_occurred_during_login');
        message.warning(errorMsg);
        setLoading(false);
      } else {
        message.error(t('facebook_login_was_cancelled'));
        setLoading(false);
      }
    }, null, facebookLoginScope);
  }, [setLoading, loginWithFacebookCallback, t]);

  const handleForgotPasswordRedirect = () => {
    navigate('/forgot-password');
  };

  const handleRegisterRedirect = () => {
    navigate('/register');
  };

  return (
    <CommonWrapper>
      <Form
        name="login"
        style={{ maxWidth: '100%', margin: '0 auto' }}
        onFinish={onFinish}
      >
        <Form.Item
          name="username"
          rules={[
            { required: true, message: t('please_input_your_email') },
            { type: 'email', message: t('please_enter_a_valid_email_address') },
          ]}
        >
          <Input
            prefix={<UserOutlined />}
            placeholder={t('email')}
            size="large"
            style={{ borderRadius: '5px' }}
          />
        </Form.Item>
        <Form.Item
          name="password"
          style={{ marginBottom: 24 }}
          rules={[
            { required: true, message: t('please_input_your_password') },
            { min: 6, message: t('password_must_be_at_least_6_characters_long') },
          ]}
        >
          <Input.Password
            prefix={<LockOutlined />}
            type="password"
            placeholder={t('password')}
            size="large"
            style={{ borderRadius: '5px' }}
          />
        </Form.Item>
        {siteKey && (
          <Turnstile
            key={`turnstile-${isDarkMode ? 'dark' : 'light'}`}
            sitekey={siteKey}
            appearance="always"
            theme={isDarkMode ? 'dark' : 'light'}
            language={captchaLang}
            autoResetOnExpire
            className="turnstile-embed"
            style={{ width: '100%' }}
            userRef={turnstileRef}
            responseField={false}
            onVerify={(token) => setCaptchaToken(token)}
            onExpire={() => setCaptchaToken('')}
            onError={() => setCaptchaToken('')}
          />

        )}
        <Form.Item style={{ marginTop: 16, marginBottom: 8 }}>
          <div style={{ textAlign: 'right' }}>
            <Button
              type="link"
              style={{ fontSize: '0.9em', padding: 0 }}
              onClick={handleForgotPasswordRedirect}
            >
              {t('forgot_password')}
            </Button>
          </div>
        </Form.Item>
        <Form.Item style={{ marginBottom: 8 }}>
          <Button
            block
            type="primary"
            htmlType="submit"
            size="large"
            loading={loginType === 'platform' && loading}
            style={{
              background: '#4A90E2',
              borderColor: '#4A90E2'
            }}
          >
            {t('log_in')}
          </Button>
        </Form.Item>
      </Form>
      <div style={{ textAlign: 'center', marginTop: '10px' }}>
        <Text>{t('dont_have_an_account')} </Text>
        <Button
          type="link"
          style={{ fontSize: '0.9em', padding: 0 }}
          onClick={handleRegisterRedirect}
        >
          {t('register_now')}
        </Button>
      </div>
      <Divider style={{ margin: '0 0 8px 0' }}>
        <Text type="secondary" style={{ fontSize: '0.9em' }}>{t('or')}</Text>
      </Divider>

      <Button
        block
        size="large"
        onClick={handleFacebookLogin}
        loading={loginType === 'facebook' && loading}
        style={{
          background: '#1877f2',
          borderColor: '#1877f2',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px'
        }}
      >
        <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
        </svg>
        {t('continue_with_facebook')}
      </Button>
    </CommonWrapper>
  );
};

export default Login;
